import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { useGetItemQuery, useGetAllCategoriesQuery, useGetSubCategoriesByCategoryQuery, useCreateItemMutation, useUpdateItemMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageDropzone from '../../../components/ImageDropzone';
import { showSuccess, showError } from '../../../utils/toast';

const AdminItemForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetItemQuery(id!, { skip: !isEdit });
  const { data: categoriesData } = useGetAllCategoriesQuery(undefined);
  const [createItem, { isLoading: isCreating }] = useCreateItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateItemMutation();

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    category_id: '',
    sub_category_ids: [] as number[],
    description: '',
    description_ar: '',
    images: [] as File[],
  });
  
  const [existingImages, setExistingImages] = useState<string[]>([]);
  
  // Fetch subcategories when category is selected
  const { data: subCategoriesData } = useGetSubCategoriesByCategoryQuery(
    formData.category_id || 0,
    { skip: !formData.category_id }
  );

  useEffect(() => {
    if (isEdit && data?.data) {
      const item = data.data;
      setFormData({
        name: item.name || '',
        name_ar: item.name_ar || '',
        category_id: item.category_id || '',
        sub_category_ids: item.sub_categories?.map((sc: any) => sc.id || sc.sub_category_id) || [],
        description: item.description || '',
        description_ar: item.description_ar || '',
        images: [],
      });
      // Extract image filenames from full URLs
      if (item.images && Array.isArray(item.images)) {
        const imageFilenames = item.images.map((img: string) => {
          return img;
        });
        setExistingImages(imageFilenames);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      // Reset subcategories when category changes
      if (name === 'category_id') {
        newData.sub_category_ids = [];
      }
      return newData;
    });
  };

  const handleSubCategoryChange = (selectedOptions: any) => {
    const subCategoryIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData(prev => ({ ...prev, sub_category_ids: subCategoryIds }));
  };

  const handleImagesChange = (files: File[]) => {
    setFormData(prev => ({ ...prev, images: files }));
  };

  const handleRemoveExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('name_ar', formData.name_ar || '');
      submitData.append('category_id', formData.category_id);
      formData.sub_category_ids.forEach((id) => {
        submitData.append('sub_category_ids[]', id.toString());
      });
      submitData.append('description', formData.description || '');
      submitData.append('description_ar', formData.description_ar || '');
      
      // Append images
      formData.images.forEach((file) => {
        submitData.append('images[]', file);
      });

      if (isEdit) {
        const result = await updateItem({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || 'Item updated successfully!');
      } else {
        const result = await createItem(submitData).unwrap();
        showSuccess(result.message || 'Item created successfully!');
      }
      
      navigate('/admin/items');
    } catch (err: any) {
      showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} item. Please try again.`);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const categories = categoriesData?.data || [];
  const subCategories = subCategoriesData?.data?.sub_categories || subCategoriesData?.data || [];
  
  // Format subcategories for react-select
  const subCategoryOptions = subCategories.map((subCat: any) => ({
    value: subCat.id,
    label: `${subCat.title || subCat.name} - ${subCat.title_ar || subCat.name_ar || ''}`,
  }));
  
  // Get selected subcategories for react-select
  const selectedSubCategories = subCategoryOptions.filter((option: any) => 
    formData.sub_category_ids.includes(option.value)
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit Item' : 'Add New Item'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Dashboard</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/items'); }} className="text-muted text-hover-primary">Items</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2"> Name</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter Item Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2"> Name Arabic</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter Item Name in Arabic"
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Category:</label>
                      <select
                        className="form-select form-control-solid"
                        data-control="select2"
                        data-hide-search="true"
                        data-placeholder="Select Category"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((category: any) => (
                          <option key={category.id} value={category.id}>{category.name} ({category.name_ar})</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">SubCategory:</label>
                      <Select
                        isMulti
                        isSearchable
                        options={subCategoryOptions}
                        value={selectedSubCategories}
                        onChange={handleSubCategoryChange}
                        placeholder="Select SubCategories"
                        isDisabled={!formData.category_id}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '48px',
                            borderColor: '#e4e6ef',
                            '&:hover': {
                              borderColor: '#e4e6ef',
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#f1faff',
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: '#009ef7',
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#009ef7',
                            '&:hover': {
                              backgroundColor: '#009ef7',
                              color: 'white',
                            },
                          }),
                        }}
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Description</label>
                      <textarea
                        name="description"
                        className="form-control form-control-solid"
                        placeholder="Write Description"
                        rows={3}
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Description Arabic</label>
                      <textarea
                        name="description_ar"
                        className="form-control form-control-solid"
                        placeholder="Write Description in Arabic"
                        rows={3}
                        value={formData.description_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <ImageDropzone
                        maxImages={10}
                        existingImages={existingImages}
                        onImagesChange={handleImagesChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        label="Upload Images"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/items')}>
                      Discard
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">Submit</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminItemForm;

